import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import { useLazyQuery } from '@apollo/client';
import { InputAdornment, TextField, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import WrapTextIcon from '@mui/icons-material/WrapText';

import '../../../ProjectTour/lib';
import { CAN_ADD_PROJECT, CAN_VIEW_PROJECT } from '../../../constants/permissions';
import { GET_PROJECTS } from '../../../apis/project';
import { PROJECT } from '../../../constants/routes';
import { useAuth } from '../../../context/AuthContext';
import * as projectImg from '../../../components/NavBar/NavbarIcons';
import AddProject from '../../../components/AddProject';
import Button from '../../../components/atoms/button';
import PageName from '../../../components/Heading/ProjectTitle';
import ProjectContext from '../../../context/ProjectContext';
import ProjectWarningContext from '../../../context/ProjectWarningContext';
import useLoader from '../../../context/LoaderContext';
import useProjectVersionHistory from '../../../context/VersionHistoryContext';
import useWorkspace from '../../../context/WorkspaceContext';
import WorkspaceOptions from '../../../components/molecules/workspace-options';

const StyledImage = styled.img`
  max-width: 500px;
  width: 100%;
`;

const CustomTypo = styled(Typography)`
  &.MuiTypography-root {
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
    text-align: center;
    color: rgba(0, 0, 0, 0.56);
    max-width: 491px;
  }
`;

const ProjectTableCreatedByColumn = (params) => {
  const { data } = params;

  if (!data.createdBy) return <span style={{ color: '#dd0202' }}>Deleted User</span>;

  return <span>{data.createdBy}</span>;
};

const ProjectList = () => {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  const { authUser } = useAuth();
  const { isLoading, setIsLoading } = useLoader();
  const { setProjectTour, showProjectTour, userPermissions } = useWorkspace();
  const { workspace, isFreeTrial, freeAddProjectsLimit, setShowUpgradePlan } = useWorkspace();
  const { setReadOnlyMode, setSelectedListItem, setShowVersionHistoryBar } = useProjectVersionHistory();
  const { dispatch } = useContext(ProjectWarningContext);
  const { dispatch: projectDispatch } = useContext(ProjectContext);

  const [gridApi, setGridApi] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [rowData, setRowData] = useState();
  const [wrapText, setWrapText] = useState(localStorage.getItem('Text') === 'true');
  const navigate = useNavigate();

  const permissions = useMemo(() => {
    if (userPermissions !== undefined) {
      if (userPermissions.permissions !== undefined) {
        return userPermissions.permissions.map((x) => x);
      }
    }
    return [];
  }, [userPermissions]);

  const onClickHandler = () => {
    if (isFreeTrial && (rowData?.length || 0) >= freeAddProjectsLimit) setShowUpgradePlan(true);
    else setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const [getProjects] = useLazyQuery(GET_PROJECTS, { fetchPolicy: 'network-only' });

  const getProjectList = async () => {
    if (authUser && workspace && permissions) {
      if (permissions.includes(CAN_VIEW_PROJECT)) {
        if (!isLoading) setIsLoading(true);
        const { error, loading, data } = await getProjects();
        dispatch({ type: 'SHOW_RISK_BAR', payload: { showRiskBar: false } });
        setShowVersionHistoryBar(false);
        setReadOnlyMode(false);
        setSelectedListItem(null);
        if (!error && !loading && data?.getProjectsByUser && !rowData) {
          const projectData = data?.getProjectsByUser.map((row) => ({
            id: row.id,
            name: row.name,
            key: row.key,
            description: row.description,
            createdBy: row.displayName,
            isDisabled: row.isDisabled,
          }));
          setRowData(projectData);
          setIsLoading(false);
        }
      }
      document.title = `${workspace.name} | All Projects`;
    }
  };

  const showDescription = () => {
    setWrapText(!wrapText);
  };

  const onCellClickedHandler = async (e) => {
    if (e.column.colId === 'name' && e.data.isDisabled === false) {
      navigate(`/${PROJECT}/${e.data.id}`);
      await projectDispatch({ type: 'SET_PROJECT_ID', payload: { projectId: e.data.id } });
    }
  };

  useEffect(() => {
    if (authUser) {
      if (isLoading === false && showProjectTour === true) {
        const tourOptionsForAdmin = {
          options: {
            darkLayerPersistence: true,
            next: 'Next',
            prev: 'Previous',
            finish: 'Okay',
            mobileThreshold: 768,
          },
          tips: [
            {
              title: 'Welcome to Simple Plan !',
              description: 'To continue this short tour click here to create a new project. ',
              selector: '.btn-newProject',
              x: 5,
              y: 50,
              offx: 0,
              offy: 0,
              position: 'left',
              onSelected: false,
            },
          ],
        };
        const tourOptionsForEmployee = {
          options: {
            darkLayerPersistence: true,
            next: 'Next',
            prev: 'Previous',
            finish: 'Okay',
            mobileThreshold: 768,
          },
          tips: [
            {
              title: 'Welcome to Simple Plan !',
              description: ' Click on this project to navigate through the tour.',
              selector: '.search-input',
              x: 25,
              y: 280,
              offx: 0,
              offy: 0,
              position: 'right',
              onSelected: false,
            },
          ],
        };
        try {
          if ([1, 2].includes(userPermissions.roleId)) {
            window.ProductTourJS.init(tourOptionsForAdmin);
            window.ProductTourJS.setPositions();
            window.ProductTourJS.start();
            setProjectTour(false);
          } else if ([3].includes(userPermissions.roleId) && rowData && rowData.length !== 0) {
            window.ProductTourJS.init(tourOptionsForEmployee);
            window.ProductTourJS.start();
            setProjectTour(false);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [isLoading, authUser]);

  const columnDef = [
    { headerName: 'Id', field: 'id', hide: true },
    {
      headerName: 'Project Name',
      field: 'name',
      headerClass: 'first-header-cell',
      cellClass: 'first-cell',
      resizable: true,
    },
    { headerName: 'Key', field: 'key', width: 70 },
    {
      headerName: 'Description', field: 'description', resizable: true, autoHeight: true, wrapText,
    },
    { headerName: 'Created by', field: 'createdBy', cellRenderer: ProjectTableCreatedByColumn },
  ];
  const [columnDefs, setColumnDefs] = useState(columnDef);

  const getRowClass = (params) => {
    if (params.data.isDisabled) {
      return 'disabled-row';
    }
    return '';
  };

  useEffect(() => {
    getProjectList();
  }, [authUser, workspace, permissions]);

  useEffect(() => {
    localStorage.setItem('Text', wrapText);
    if (gridApi && Object.keys(gridApi).length > 0) {
      setColumnDefs((prevDefs) => prevDefs.map((col) => {
        if (col.field === 'description') {
          return { ...col, wrapText };
        }
        return col;
      }));
    }
  }, [wrapText]);

  const onFilterTextBoxChanged = (e) => {
    const dataFilter = e.target.value;
    if (rowData?.length) gridApi.setQuickFilter(dataFilter);
  };

  return (
    <>
      <div className="page-header">
        <WorkspaceOptions />
        <PageName name="Projects" />
        <div className="header-layout">
          <div className="header-search-and-text-wrap">
            <div className="top-search-btn">
              <TextField
                className="search-input"
                type="search"
                onChange={onFilterTextBoxChanged}
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment style={{ color: 'rgba(0, 0, 0, 0.4)' }} position="start">
                      <SearchIcon className="search-icon" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <Button className={`toggle-expand-btn ${wrapText ? 'wrap-open' : ''}`} onClick={showDescription}>
              <WrapTextIcon className="wraptext-ic" />
            </Button>
          </div>
          {
            permissions.includes(CAN_ADD_PROJECT) && (
              <div>
                <AddProject setOpenDialog={setOpenDialog} openToggle={openDialog} handleClose={handleClose} />
                <Button style={{ alignSelf: 'end' }} primary startIcon={<AddIcon fontSize="small" />} onClick={onClickHandler}>
                  <strong>New Project</strong>
                </Button>
              </div>
            )
          }
        </div>
      </div>
      {
        !isLoading && rowData?.length === 0 ? (
          <Grid>
            <Grid container style={{ padding: '28px' }}>
              <Grid item style={{ marging: '20px auto 0' }} xs={12} sm={12} md={12} lg={12} xl={12} align="center">
                <StyledImage src={projectImg.noProjectImg} alt="noprojectart" draggable="false" />
              </Grid>
              <Grid item style={{ marging: '0 auto' }} xs={12} sm={12} md={12} lg={12} xl={12} align="center">
                {permissions.includes(CAN_ADD_PROJECT) ? (
                  <CustomTypo sx={{ mx: 'auto' }}>
                    You don`t have any projects created. Click the button below to create your first
                    project.
                  </CustomTypo>
                ) : (
                  <CustomTypo sx={{ mx: 'auto' }}>You don`t have any projects. </CustomTypo>
                )}
              </Grid>
              <Grid
                item
                sx={{
                  mx: 'auto',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '20px',
                }}
              >
                {permissions.includes(CAN_ADD_PROJECT) && (
                  <Button primary startIcon={<AddIcon fontSize="small" />} onClick={onClickHandler}>
                    <strong>New Project</strong>
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          rowData && rowData.length !== 0 &&
          <div style={containerStyle}>
            <div style={{ height: '100%', width: '100%', boxSizing: 'border-box' }}>
              <div style={gridStyle} className="ag-theme-alpine">
                <AgGridReact
                  className="cell-click common-table-grid"
                  gridOptions={{
                    suppressNoRowsOverlay: true,
                    rowHeight: 40,
                    headerHeight: 40,
                    defaultColDef: {
                      resizable: false,
                      suppressMovable: true,
                    },
                  }}
                  rowBuffer
                  sizeColumnsToFit
                  rowData={rowData}
                  columnDefs={columnDefs}
                  domLayout="autoHeight"
                  animateRows
                  onCellClicked={onCellClickedHandler}
                  debounceVerticalScrollbar
                  onGridReady={(gridParams) => {
                    setGridApi(gridParams.api);
                  }}
                  getRowClass={getRowClass}
                />
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default ProjectList;
